import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCw, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

import { InviteMemberDialog } from '@/components/settings/invite-member-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi, type TeamInvitation, type TeamMember } from '@/lib/admin-api';
import { resourceLabel } from '@/lib/staff-resources';

/**
 * Team members — the settings card, ported from ctrl-saas's
 * `/home/[account]/settings/team`.
 *
 * Everything here goes through the admin API rather than PostgREST. Listing
 * members means reading each member's *personal* account for their name and
 * email, which RLS only allows through a per-row EXISTS over memberships; and
 * inviting needs the Resend key and the link-signing key, neither of which may
 * reach the browser. See `apps/api/src/routes/team.ts`.
 */
export function TeamSettings({ accountId }: { accountId: string }) {
  const team = useQuery({
    queryKey: ['team', accountId],
    queryFn: () => adminApi.team.get(accountId),
  });

  if (team.isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (team.isError) {
    return (
      <p className="text-destructive text-sm">
        Could not load the team: {(team.error as Error).message}
      </p>
    );
  }

  const { members, invitations, roles } = team.data;

  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium">
              Members <span className="text-muted-foreground">({members.length})</span>
            </h3>
            <p className="text-muted-foreground text-xs">Everyone with access to this workspace.</p>
          </div>

          <InviteMemberDialog accountId={accountId} roles={roles} />
        </header>

        <MembersTable members={members} />
      </section>

      <section className="flex flex-col gap-4">
        <div>
          <h3 className="text-sm font-medium">
            Pending invitations{' '}
            <span className="text-muted-foreground">({invitations.length})</span>
          </h3>
          <p className="text-muted-foreground text-xs">
            Invitations last seven days. Resending starts the clock again.
          </p>
        </div>

        <InvitationsTable accountId={accountId} invitations={invitations} />
      </section>
    </div>
  );
}

function MembersTable({ members }: { members: TeamMember[] }) {
  if (!members.length) {
    return <EmptyRow>No members yet.</EmptyRow>;
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Capabilities</TableHead>
            <TableHead className="text-right">Joined</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {[...members]
            // Most elevated first, then by name, so the owners are at the top
            // where you look for them rather than wherever they happened to join.
            .sort(
              (a, b) =>
                a.roleHierarchyLevel - b.roleHierarchyLevel ||
                displayName(a).localeCompare(displayName(b)),
            )
            .map((member) => (
              <TableRow key={member.userId}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {/* The same colour that identifies them on the queue and
                        delivery boards, so the two screens agree. */}
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: member.iconColor ?? 'var(--muted-foreground)' }}
                      aria-hidden
                    />

                    <div className="min-w-0">
                      <div className="truncate font-medium">{displayName(member)}</div>
                      <div className="text-muted-foreground truncate text-xs">
                        {member.email ?? '—'}
                      </div>
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="secondary" className="capitalize">
                      {member.role}
                    </Badge>

                    {member.isPrimaryOwner && <Badge variant="outline">Primary owner</Badge>}
                  </div>
                </TableCell>

                <TableCell>
                  <Capabilities member={member} />
                </TableCell>

                <TableCell className="text-muted-foreground text-right text-xs whitespace-nowrap">
                  {formatDate(member.createdAt)}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Capabilities are meaningless for a role that already holds every one of them,
 * so say that instead of rendering ten badges that grant nothing. `members.manage`
 * is what `jwt_has_resource` short-circuits on, and only `owner` is seeded with it.
 */
function Capabilities({ member }: { member: TeamMember }) {
  if (member.roleHierarchyLevel === 1) {
    return <span className="text-muted-foreground text-xs">All</span>;
  }

  if (!member.resources.length) {
    return <span className="text-muted-foreground text-xs">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {member.resources.map((resource) => (
        <Badge key={resource} variant="outline" className="font-normal">
          {resourceLabel(resource)}
        </Badge>
      ))}
    </div>
  );
}

function InvitationsTable({
  accountId,
  invitations,
}: {
  accountId: string;
  invitations: TeamInvitation[];
}) {
  const queryClient = useQueryClient();

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['team', accountId] });
  }

  const resend = useMutation({
    mutationFn: (id: number) => adminApi.team.resendInvitation(accountId, id),
    onSuccess: (result) => {
      if (result.emailSent) {
        toast.success('Invitation resent.');
      } else {
        toast.warning('Expiry extended, but the email could not be sent.');
      }

      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => adminApi.team.revokeInvitation(accountId, id),
    onSuccess: () => {
      toast.success('Invitation revoked.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!invitations.length) {
    return <EmptyRow>No pending invitations.</EmptyRow>;
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invitee</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Invited by</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="w-0" />
          </TableRow>
        </TableHeader>

        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell>
                <div className="min-w-0">
                  <div className="truncate font-medium">{invitation.email}</div>

                  {(invitation.firstName || invitation.lastName) && (
                    <div className="text-muted-foreground truncate text-xs">
                      {[invitation.firstName, invitation.lastName].filter(Boolean).join(' ')}
                    </div>
                  )}
                </div>
              </TableCell>

              <TableCell>
                <Badge variant="secondary" className="capitalize">
                  {invitation.role}
                </Badge>
              </TableCell>

              <TableCell className="text-muted-foreground text-xs">
                {invitation.inviterName ?? invitation.inviterEmail ?? '—'}
              </TableCell>

              <TableCell className="text-xs whitespace-nowrap">
                {invitation.isExpired ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : (
                  <span className="text-muted-foreground">{formatDate(invitation.expiresAt)}</span>
                )}
              </TableCell>

              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(invitation.id)}
                  >
                    <RotateCw className="size-3.5" />
                    Resend
                  </Button>

                  {/*
                    No confirmation step: revoking an invitation destroys
                    nothing that cannot be recreated by inviting again, and a
                    dialog on top of two dialogs is worse than the mistake.
                  */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    <Trash2 className="size-3.5" />
                    <span className="sr-only">Revoke invitation for {invitation.email}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-6 text-sm">
      <Users className="size-4" />
      {children}
    </div>
  );
}

function displayName(member: TeamMember): string {
  const staffName = [member.firstName, member.lastName].filter(Boolean).join(' ');

  // The store's own record of an employee's legal name wins over whatever they
  // set on their personal account — that is the point of keeping it separately.
  return staffName || member.name || member.email || member.userId;
}

function formatDate(iso: string): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
